import { inject, Injectable } from '@angular/core'

import {
  auditTime,
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  skip,
  take,
  takeUntil,
  timer,
  withLatestFrom,
} from 'rxjs'
import { switchMap } from 'rxjs/operators'

import { ChatEvents } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { ONE_MINUTE_MS } from '@ti/core/common/constants'
import { Memoize } from '@ti/core/common/decorators'
import { injectDestroy$ } from '@ti/core/common/utils'
import { IChatFacade } from '@ti/core/contracts/chat'
import { DriversManager } from '@ti/shared/drivers/services'

import { SendMessageData, UiThreadData } from '../contracts'
import { ChatNotificationsHandler } from './chat-notifications.handler'
import { ChatThreadsStore } from './chat-threads.store'
import { ChatUpdatesListener } from './chat-updates.listener'

@Injectable()
export abstract class ChatFacade implements IChatFacade {
  protected readonly api = inject(ApiService)
  protected readonly threadsStore = inject(ChatThreadsStore)
  protected readonly chatUpdatesListener = inject(ChatUpdatesListener)
  protected readonly chatNotificationsHandler = inject(ChatNotificationsHandler)

  protected readonly destroy$ = injectDestroy$()

  protected readonly store = {
    isLoading$: new BehaviorSubject<boolean>(true),
    unreadCounters$: new BehaviorSubject(new Map<string, number>()),
    driverLastOnlineAt$: new BehaviorSubject(new Map<string, number>()),
  }

  protected readonly DRIVER_STATUSES_TTL = 5 * ONE_MINUTE_MS
  protected isLoadingUnreadCounters = false

  public constructor() {
    Promise.resolve().then(() => this.init())
  }

  @Memoize()
  public get state$() {
    return {
      isLoading: this.store.isLoading$.asObservable(),
      unreadCounters: this.store.unreadCounters$.asObservable(),
      threads: this.createThreadsDataStream(),
      totalUnreadCount: this.createTotalUnreadCountStream(),
    }
  }

  public async sendMessage(threadId: string, data: SendMessageData) {
    if (data.message) {
      return this.execAddMessage(threadId, data.message, data.id)
    }
    if (data.attachments) {
      return this.execSendAttachment(threadId, data.attachments[0].data, data.id)
    }
  }

  protected abstract execAddMessage(threadId: string, message: string, id?: string): Promise<any>

  protected abstract execSendAttachment(
    threadId: string,
    file: File | Blob,
    id?: string,
  ): Promise<any>

  public async deleteMessage(threadId: string, messageId: string) {
    try {
      await this.execDeleteMessage(threadId, messageId)
    } catch (error) {
      console.error('Failed to delete message as read:', error)
    }
  }

  protected abstract execDeleteMessage(threadId: string, messageId: string): Promise<any>

  public async markThreadAsRead(threadId: string) {
    try {
      await this.execMarkAsRead(threadId)
      // Reset the unread counter for the thread
      const unreadCounters = this.store.unreadCounters$.getValue()
      unreadCounters.set(threadId, 0)
      this.store.unreadCounters$.next(unreadCounters)
    } catch (error) {
      console.error('Failed to mark thread as read:', error)
    }
  }

  protected abstract execMarkAsRead(threadId: string): Promise<any>

  public async setIsThreadMuted(threadId: string, isMuted: boolean) {
    if (isMuted) {
      await this.execMuteThread(threadId)
    } else {
      await this.execUnmuteThread(threadId)
    }
    return this.threadsStore.refresh()
  }

  public async massSetIsThreadMuted(isMuted: boolean) {
    const threads = await this.threadsStore.getThreads()
    for (const thread of threads) {
      if (isMuted && !thread.settings.mute) {
        await this.execMuteThread(thread.id)
      } else if (!isMuted && thread.settings.mute) {
        await this.execUnmuteThread(thread.id)
      }
    }
    return this.threadsStore.refresh()
  }

  protected abstract execMuteThread(threadId: string): Promise<unknown> | unknown

  protected abstract execUnmuteThread(threadId: string): Promise<unknown> | unknown

  public reloadThreads() {
    return this.threadsStore.refresh()
  }

  protected async init() {
    this.initDataLoading()
    this.initChatUpdatesHandler()
    this.chatNotificationsHandler.init()

    this.threadsStore.state$.data.pipe(takeUntil(this.destroy$)).subscribe((threads) => {
      threads.forEach((thread) => {
        // Update thread mute settings
        if (thread.settings.mute) {
          this.chatNotificationsHandler.config.mutedThreads.add(thread.id)
        } else {
          this.chatNotificationsHandler.config.mutedThreads.delete(thread.id)
        }

        // Enable notifications for all chats with drivers
        if (!this.chatNotificationsHandler.config.showNotificationsForThreads.has(thread.id)) {
          this.chatNotificationsHandler.config.showNotificationsForThreads.add(thread.id)
        }
      })
    })
  }

  protected async loadUnreadCounters(threadIds?: string[]) {
    const threads = await this.threadsStore.getThreads(threadIds)
    if (threads?.length) {
      if (!this.isLoadingUnreadCounters) {
        this.isLoadingUnreadCounters = true
      } else {
        console.debug('CHAT Already bulk-loading counters')
        return
      }
      try {
        const result = await firstValueFrom(this.execGetBulkUnreadCount(threads.map((t) => t.id)))
        const unreadCounters = this.store.unreadCounters$.getValue()
        Object.entries(result).forEach(([threadId, count]) => unreadCounters.set(threadId, count))
        this.store.unreadCounters$.next(unreadCounters)
      } catch (e) {
        console.error('Failed to bulk-load chat unread counters', e)
      }
    }
    this.isLoadingUnreadCounters = false
  }

  protected abstract execGetBulkUnreadCount(threadIds: string[]): Observable<Record<string, number>>

  protected initDataLoading() {
    this.threadsStore.state$.data.pipe(skip(1), takeUntil(this.destroy$)).subscribe(async () => {
      this.store.isLoading$.next(true)
      await Promise.all([this.loadUnreadCounters()])
      this.store.isLoading$.next(false)
    })
  }

  protected initChatUpdatesHandler() {
    this.chatUpdatesListener.onUpdates$
      .pipe(takeUntil(this.destroy$), withLatestFrom(this.threadsStore.state$.data))
      .subscribe(([event, threads]) => {
        if (
          event.threadId &&
          (event.type === ChatEvents.MessageSent || event.type === ChatEvents.MessageDeleted)
        ) {
          this.loadUnreadCounters([event.threadId]).catch((e) => {
            console.error(`Cannot load unread counters`, e)
          })

          if (event.type === ChatEvents.MessageSent) {
            this.threadsStore.refresh([event.threadId]).catch((e) => {
              console.error(`Cannot load last thread message`, e)
            })
          }
        }

        if (event.type === ChatEvents.DriverOnline || event.type === ChatEvents.DriverOffline) {
          const driverLastOnlineAt = this.store.driverLastOnlineAt$.getValue()
          const relatedThread = threads.find((thread) => thread.userId === event.driverId)
          if (relatedThread) {
            driverLastOnlineAt.set(
              relatedThread.id,
              event.type === ChatEvents.DriverOnline ? event.createdAt.toMillis() : 0,
            )
            this.store.driverLastOnlineAt$.next(driverLastOnlineAt)
          }
        }
      })
  }

  protected createThreadsDataStream() {
    return combineLatest([
      this.store.unreadCounters$,
      this.store.driverLastOnlineAt$,
      this.threadsStore.state$.data,
      timer(0, 5000), // To refresh driver statuses once in a while
    ]).pipe(
      auditTime(16),
      takeUntil(this.destroy$),
      map(([unreadCounters, driverLastOnlineAt, threads]) => {
        if (!threads?.length) return []

        const DRIVER_ID_ONLINE_AFTER = Date.now() - this.DRIVER_STATUSES_TTL
        return threads
          .map((thread) => {
            return {
              ...thread,
              isOnline: (driverLastOnlineAt.get(thread.id) ?? 0) >= DRIVER_ID_ONLINE_AFTER,
              unreadCount: unreadCounters.get(thread.id),
            } as UiThreadData
          })
          .sort((a, b) => {
            if (a.lastMessage && b.lastMessage) {
              // Both have lastMessage - sort by message date descending
              return (
                new Date(b.lastMessage.createdAt).valueOf() -
                new Date(a.lastMessage.createdAt).valueOf()
              )
            } else if (a.lastMessage) {
              // a has message, b doesn't - a goes first
              return -1
            } else if (b.lastMessage) {
              // b has message, a doesn't - b goes first
              return 1
            } else {
              // Neither has message - sort by name ascending
              return a.name.localeCompare(b.name)
            }
          })
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
  }

  protected createTotalUnreadCountStream() {
    return this.store.unreadCounters$.pipe(
      takeUntil(this.destroy$),
      map((counters) => {
        return Array.from(counters.values()).reduce((total, curr) => total + curr, 0)
      }),
    )
  }
}

@Injectable()
export class FleetChatFacade extends ChatFacade {
  protected readonly driversManager = inject(DriversManager)

  protected override async init() {
    await super.init()
    this.initDriversManagerListener()
  }

  protected initDriversManagerListener() {
    this.driversManager.updated$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((value) =>
          this.threadsStore.state$.data.pipe(
            take(1),
            map((state) => {
              return state
                .filter((thread) => value.includes(thread.userId))
                .map((thread) => thread.id)
            }),
          ),
        ),
      )
      .subscribe((matchingThreadIds) => {
        this.threadsStore.refresh(matchingThreadIds)
      })
  }

  protected execAddMessage(threadId: string, message: string, id?: string) {
    return this.api.fleet.chat.addMessage(threadId, {
      id,
      message,
    })
  }

  protected execSendAttachment(threadId: string, file: File | Blob, id?: string) {
    return this.api.fleet.chat.sendAttachment(threadId, {
      id,
      file,
    })
  }

  protected execDeleteMessage(threadId: string, messageId: string) {
    return this.api.fleet.chat.deleteMessage(threadId, messageId)
  }

  protected execMarkAsRead(threadId: string) {
    return this.api.fleet.chat.markAsRead(threadId)
  }

  protected execMuteThread(threadId: string) {
    return this.api.fleet.chat.muteThread(threadId)
  }

  protected execUnmuteThread(threadId: string) {
    return this.api.fleet.chat.unmuteThread(threadId)
  }

  protected execGetBulkUnreadCount(threadIds: string[]) {
    return this.api.fleet.chat.getBulkUnreadCount$(threadIds)
  }
}
